/**
 * Strip HTML from a Kanban task's `details` field to produce a plain-text
 * preview for the task card.
 *
 * Why plain text for the card: the Kanban Add/Edit panel uses the <at>
 * employee picker which saves details as contenteditable HTML with nested
 * spans (e.g. `<span data-at-embedded=""><span data-teamio-employeeid="..." ...>Name</span></span>`).
 * Passing that HTML to vue-truncate-collapsed with `type="html"` was
 * freezing the render thread — the truncate component walks the DOM tree
 * to compute visible length, and the nested span soup (plus `&nbsp;` and
 * stale `data-v-*` scoped-style attrs) made it pathologically slow.
 *
 * Stripping tags + collapsing whitespace is a cheap, safe render path for
 * the card preview. Full HTML still renders in the Edit panel's
 * contenteditable where `<at>` actually needs it.
 *
 * Unit tests: cypress/node-tests/kanban-details-preview.test.js
 *
 * @param {string|null|undefined} raw  Raw HTML or plain-text details string
 * @param {Document} [doc]             Optional DOMParser/document for Node tests
 * @returns {string}                    Plain-text preview, whitespace-collapsed
 */
/**
 * Parse a Kanban task's `details` field into an ordered array of text/mention
 * parts for badge-style rendering on the card.
 *
 * Each part is `{ type: 'text', text }` OR `{ type: 'mention', text, empId }`.
 * Mentions are detected by walking the DOM and finding elements carrying a
 * `data-teamio-employeeid` attribute (the marker the <at> picker writes).
 *
 * Length cap: ~`maxLen` visible characters total across all parts (default 90).
 * When a part crosses the cap, its text is sliced and an ellipsis appended to
 * the last emitted part.
 *
 * @param {string|null|undefined} raw
 * @param {Document} [doc]
 * @param {number} [maxLen=90]
 * @returns {Array<{type: 'text'|'mention', text: string, empId?: string}>}
 */
export function toPreviewParts(raw, doc, maxLen = 90) {
  if (!raw || typeof raw !== 'string') return [];

  // Plain text (no tags, no entities) — single text part, length-capped.
  if (raw.indexOf('<') === -1 && raw.indexOf('&') === -1) {
    const t = raw.replace(/\s+/g, ' ').trim();
    if (!t) return [];
    return [{ type: 'text', text: t.length > maxLen ? t.slice(0, maxLen).trimEnd() + '…' : t }];
  }

  const document_ = doc || (typeof document !== 'undefined' ? document : null);
  if (!document_) {
    // Node fallback without DOM: fall back to plain-text stripping (no
    // mention boundaries preserved). The browser always has a document, so
    // this path is only hit in rare non-DOM test environments.
    const t = toPreviewText(raw);
    if (!t) return [];
    return [{ type: 'text', text: t.length > maxLen ? t.slice(0, maxLen).trimEnd() + '…' : t }];
  }

  let container;
  try {
    container = document_.createElement('div');
    container.innerHTML = raw;
  } catch (_) {
    const t = toPreviewText(raw);
    if (!t) return [];
    return [{ type: 'text', text: t.length > maxLen ? t.slice(0, maxLen).trimEnd() + '…' : t }];
  }

  const parts = [];
  let remaining = maxLen;
  let truncated = false;
  let textBuf = '';

  const flushText = () => {
    if (!textBuf) return;
    // Collapse whitespace in the text buffer, preserving a single leading/
    // trailing space so words on either side of a mention stay separated.
    const collapsed = textBuf.replace(/\s+/g, ' ');
    if (!collapsed.trim() && parts.length === 0) {
      // leading whitespace before first real content — drop
      textBuf = '';
      return;
    }
    if (remaining <= 0) {
      truncated = true;
      textBuf = '';
      return;
    }
    const slice = collapsed.length > remaining ? collapsed.slice(0, remaining) : collapsed;
    remaining -= slice.length;
    if (collapsed.length > slice.length) truncated = true;
    parts.push({ type: 'text', text: slice });
    textBuf = '';
  };

  // Walk the DOM in document order. When we hit a node carrying
  // data-teamio-employeeid, emit its textContent as a mention part (and do
  // NOT recurse into it — the name text lives at its leaf).
  const walk = (node) => {
    if (remaining <= 0) return;
    if (node.nodeType === 3 /* TEXT_NODE */) {
      textBuf += node.nodeValue || '';
      return;
    }
    if (node.nodeType !== 1 /* ELEMENT_NODE */) return;

    const empId = node.getAttribute && node.getAttribute('data-teamio-employeeid');
    if (empId) {
      flushText();
      if (remaining <= 0) { truncated = true; return; }
      const name = (node.textContent || '').replace(/\s+/g, ' ').trim();
      if (name) {
        const slice = name.length > remaining ? name.slice(0, remaining) : name;
        remaining -= slice.length;
        if (name.length > slice.length) truncated = true;
        parts.push({ type: 'mention', text: slice, empId });
      }
      return;
    }

    for (let i = 0; i < node.childNodes.length && remaining > 0; i++) {
      walk(node.childNodes[i]);
    }
  };

  for (let i = 0; i < container.childNodes.length && remaining > 0; i++) {
    walk(container.childNodes[i]);
  }
  flushText();

  // Trim trailing whitespace on the last part.
  if (parts.length > 0) {
    const last = parts[parts.length - 1];
    last.text = last.text.replace(/\s+$/, '');
    if (!last.text) parts.pop();
  }
  // Trim leading whitespace on the first part.
  if (parts.length > 0) {
    parts[0].text = parts[0].text.replace(/^\s+/, '');
    if (!parts[0].text) parts.shift();
  }

  if (truncated && parts.length > 0) {
    const last = parts[parts.length - 1];
    last.text = last.text.replace(/\s+$/, '') + '…';
  }

  return parts;
}

export function toPreviewText(raw, doc) {
  if (!raw) return '';
  if (typeof raw !== 'string') return '';
  // Plain text fast path — no HTML tags AND no HTML entities. (If either is
  // present, fall through to the DOM/regex path so entities get decoded.)
  if (raw.indexOf('<') === -1 && raw.indexOf('&') === -1) {
    return raw.replace(/\s+/g, ' ').trim();
  }

  // Browser path: use the DOM to decode entities + strip tags in one pass.
  const document_ = doc || (typeof document !== 'undefined' ? document : null);
  if (document_) {
    try {
      const tmp = document_.createElement('div');
      tmp.innerHTML = raw;
      const text = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
      return text;
    } catch (_) { /* fall through to regex fallback */ }
  }

  // Fallback (Node, no DOM): regex strip + minimal entity decode.
  return raw
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}
